import { IoCheckmarkCircle, IoWarningSharp } from "react-icons/io5";
import { BiHourglass } from "react-icons/bi";

import { modelBalanceTransferPaymentSummary } from "./modelBalanceTransferPaymentSummary";

const TRANSACTION_STATUS = {
  PENDING: "pending",
  PROCESSED: "processed",
  FAILED: "failed",
  PAYOUT_FAILED: "payout-failed",
};

export const STATUS_ICON_MAP = {
  pending: BiHourglass,
  processed: IoCheckmarkCircle,
  failed: IoWarningSharp,
};

export const localizeTransactionStatus = (transaction, formatMessage) => {
  const status = transaction.transaction_status || "unknown status"; // really should need a fallback here, but hey you never know
  switch (status) {
    case TRANSACTION_STATUS.PENDING: {
      return formatMessage({ defaultMessage: "pending", description: "Transaction status type" });
    }
    case TRANSACTION_STATUS.PROCESSED: {
      return formatMessage({ defaultMessage: "processed", description: "Transaction status type" });
    }
    case TRANSACTION_STATUS.FAILED: {
      return formatMessage({ defaultMessage: "failed", description: "Transaction status type" });
    }
    case TRANSACTION_STATUS.PAYOUT_FAILED: {
      const supportedCodes = {
        "SUPPORT-REJECTED": formatMessage({
          defaultMessage: "Support canceled",
          description: "Status message",
        }),
        "USER-REVERSED": formatMessage({
          defaultMessage: "User canceled",
          description: "Status message",
        }),
      };
      return (
        supportedCodes[transaction.transaction_data?.payout?.status] ||
        formatMessage({ defaultMessage: "Failed", description: "Status description" })
      );
    }
    default:
      return status;
  }
};

/**
 * @typedef {object} PaymentSummaryData
 * @property {string} title
 * @property {{ title: string, value: string }[]} rows
 * @property {{
 *   title: string;
 *   rows: { title: string, value: number, currency: string }[]
 * }|null} secondaryData
 */

/**
 * @function modelDepositPaymentSummary
 * @param {*} transactionData.transaction
 * @param {string|undefined} transactionData.account
 * @param {function} formatMessage
 * @returns {PaymentSummaryData}
 */
const modelDepositPaymentSummary = ({ transaction }, formatMessage) => {
  const { source_payment_method_display_string, total_amount, currency } =
    transaction.transaction_data; // @type {TxnPurchaseUserData}

  return {
    title: formatMessage({
      defaultMessage: "Deposited from",
      description: "Field label",
    }),
    rows: [
      {
        title: source_payment_method_display_string,
        value: total_amount,
        currency,
      },
    ],
    secondaryData: null,
  };
};

/**
 * @function modelPurchasePaymentSummary
 * @param {*} transactionData.transaction
 * @param {string|undefined} transactionData.account
 * @param {function} formatMessage
 * @returns {PaymentSummaryData}
 */
const modelPurchasePaymentSummary = ({ transaction }, formatMessage) => {
  const { payment_methods } = transaction.transaction_data; // @type {TxnBuyerData}
  const pms = Object.values(payment_methods || {});

  return {
    title: formatMessage({
      defaultMessage: "Paid with",
      description: "Field label",
    }),
    rows: pms.map((pm) => {
      const { authorized_amount, display_string, currency } = pm;
      return {
        title:
          display_string ||
          formatMessage({
            defaultMessage: "Account",
            description: "Field label",
          }),
        value: authorized_amount,
        currency: currency,
      };
    }),
    secondaryData: null,
  };
};

/**
 * @function modelPurchaseRecipientSummary
 * @param {*} transactionData.transaction
 * @param {string|undefined} transactionData.account
 * @param {function} formatMessage
 * @returns {PaymentSummaryData}
 */
const modelPurchaseRecipientPaymentSummary = ({ transaction }, formatMessage) => {
  const { recipient_items } = transaction.transaction_data; // @type {TxnSellerData}

  return {
    title: formatMessage({
      defaultMessage: "Deposited into",
      description: "Field label",
    }),
    rows: recipient_items.map((ri) => {
      const { payment_method_display_string, amount_received_less_fees, currency } = ri;
      return {
        title: payment_method_display_string,
        value: amount_received_less_fees,
        currency: currency,
      };
    }),
    secondaryData: null,
  };
};

/**
 * @function modelPayoutPaymentSummary
 * @param {*} transactionData.transaction
 * @param {string|undefined} transactionData.account
 * @param {function} formatMessage
 * @returns {PaymentSummaryData}
 */
const modelPayoutPaymentSummary = ({ transaction }, formatMessage) => {
  const {
    destination_payment_method_display_string,
    payout_amount_less_fee,
    payout_amount_less_fee_currency
  } = transaction.transaction_data; // @type {TxnPurchaseUserData}

  return {
    title: formatMessage({
      defaultMessage: "Payout to",
      description: "Field label",
    }),
    rows: [
      {
        title: destination_payment_method_display_string,
        value: payout_amount_less_fee,
        currency: payout_amount_less_fee_currency,
      },
    ],
    secondaryData: null,
  };
};

/**
 * @function modelTokenPaymentSummary
 * @param {*} transactionData.transaction
 * @param {string|undefined} transactionData.account
 * @param {function} formatMessage
 * @returns {PaymentSummaryData}
 */
const modelTokenPaymentSummary = ({ transaction }, formatMessage) => {
  const {
    source_payment_method_provider,
    source_payment_method_display_string,
    destination_payment_method_display_string,
    total_amount,
    token_exchange,
    currency,
  } = transaction.transaction_data; // @type {TokenPurchaseData}
  const title =
    transaction.transaction_type === "token_purchase"
      ? formatMessage({
          defaultMessage: "Paid with",
          description: "Field label",
        })
      : formatMessage({
          defaultMessage: "Transferred from",
          description: "Field label",
        });

  const rows = [
    {
      title: `${
        source_payment_method_provider === "pobo"
          ? formatMessage({
              defaultMessage: "Processed by",
              description: "Field label",
            })
          : ""
      } ${source_payment_method_display_string}`,
      value: source_payment_method_provider === "pobo" ? "" : total_amount,
      currency,
    },
  ];

  return {
    title,
    rows,
    secondaryData: {
      title: formatMessage({
        defaultMessage: "Deposited into",
        description: "Field label",
      }),
      rows: [
        {
          title: destination_payment_method_display_string,
          value: token_exchange.user_received_amount,
          currency: token_exchange.user_received_currency,
        },
      ],
    },
  };
};

/**
 * @function modelRefundPaymentSummary
 * @param {*} transactionData.transaction
 * @param {string|undefined} transactionData.account
 * @param {function} formatMessage
 * @returns {PaymentSummaryData}
 */
const modelRefundPaymentSummary = ({ transaction }, formatMessage) => {
  const { refund_payment_methods } = transaction.transaction_data; // @type {RefundBuyerData}
  const rows = (refund_payment_methods || []).map((pm) => {
    const { description, source_payment_method_provider, amount_received, currency } = pm;
    const isPobo = source_payment_method_provider === "pobo";
    return {
      title:
        description ||
        formatMessage({
          defaultMessage: "Account",
          description: "Field label",
        }),
      value: isPobo ? "" : amount_received,
      currency,
    };
  });

  return {
    title: formatMessage({
      defaultMessage: "Refund to",
      description: "Field label",
    }),
    rows,
    secondaryData: null,
  };
};

export const modelPaymentSummary = ({ transaction, accountId }, formatMessage) => {
  const { transaction_type } = transaction;
  switch (transaction_type) {
    case "balance_transfer":
      return modelBalanceTransferPaymentSummary({ transaction, accountId }, formatMessage);
    case "deposit":
      return modelDepositPaymentSummary({ transaction, accountId }, formatMessage);
    case "user_purchase":
    case "user_purchase_escrow":
      return modelPurchasePaymentSummary({ transaction, accountId }, formatMessage);
    case "user_purchase_recipient":
      return modelPurchaseRecipientPaymentSummary({ transaction, accountId }, formatMessage);
    case "payout":
      return modelPayoutPaymentSummary({ transaction, accountId }, formatMessage);
    case "token_purchase":
    case "token_convert":
      return modelTokenPaymentSummary({ transaction, accountId }, formatMessage);
    case "refund":
      return modelRefundPaymentSummary({ transaction, accountId }, formatMessage);
    default:
      return null;
  }
};
